import { Component } from '@theme/component';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { enqueueCartMutation } from '@theme/cart-mutation-queue';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const submitButton = form.querySelector('.cart-discount__button');

    try {
      await enqueueCartMutation(async () => {
        const existingDiscounts = this.#existingDiscounts();
        if (existingDiscounts.includes(discountCodeValue)) return;

        // Loading spinner on Apply while the cart update is in flight (matches
        // the Add to Cart button). Cleared in `finally`; on success the section
        // morph re-renders a fresh button anyway.
        if (submitButton instanceof HTMLElement) submitButton.classList.add('is-loading');

        cartDiscountError.classList.add('hidden');
        cartDiscountErrorDiscountCode.classList.add('hidden');
        cartDiscountErrorShipping.classList.add('hidden');

        const config = fetchConfig('json', {
          body: JSON.stringify({
            discount: [...existingDiscounts, discountCodeValue].join(','),
            sections: [this.dataset.sectionId],
          }),
        });

        const response = await fetch(Theme.routes.cart_update_url, config);
        if (!response.ok) throw new Error(`Discount update failed with status ${response.status}`);

        const data = await response.json();

        if (
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === false;
          })
        ) {
          discountCode.value = '';
          this.#handleDiscountError('discount_code');
          return;
        }

        const newHtml = data.sections?.[this.dataset.sectionId];
        if (typeof newHtml !== 'string') throw new Error('Cart discount section was not rendered');
        const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
        const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
        const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
        if (section) {
          const codes = Array.from(discountCodes)
            .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
            .filter(Boolean);
          // Before morphing, we need to check if the shipping discount is applicable in the UI
          // we check the liquid logic compared to the cart payload to assess whether we leveraged
          // a valid shipping discount code.
          if (
            codes.length === existingDiscounts.length &&
            codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
            data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
              return discount.code === discountCodeValue && discount.applicable === true;
            })
          ) {
            this.#handleDiscountError('shipping');
            discountCode.value = '';
            return;
          }
        }

        document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      });
    } catch (error) {
      console.error(error);
      await sectionRenderer.renderSection(this.dataset.sectionId, { cache: false });
    } finally {
      if (submitButton instanceof HTMLElement) submitButton.classList.remove('is-loading');
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    try {
      await enqueueCartMutation(async () => {
        const existingDiscounts = this.#existingDiscounts();
        const index = existingDiscounts.indexOf(discountCode);
        if (index === -1) return;

        existingDiscounts.splice(index, 1);

        const config = fetchConfig('json', {
          body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
        });

        const response = await fetch(Theme.routes.cart_update_url, config);
        if (!response.ok) throw new Error(`Discount removal failed with status ${response.status}`);

        const data = await response.json();
        const sectionHtml = data.sections?.[this.dataset.sectionId];
        if (typeof sectionHtml !== 'string') throw new Error('Cart discount section was not rendered');

        document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      });
    } catch (error) {
      console.error(error);
      await sectionRenderer.renderSection(this.dataset.sectionId, { cache: false });
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
